import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { SiweMessage } from "siwe";
import { useAccount, useChainId, useConnect, useDisconnect, useSignMessage, useSwitchChain } from "wagmi";
import { Page, FlexContainer } from "../components/styled";
import { BSC_CHAIN_ID } from "../service/web3/wagmi";
import { getSiweNonce, loginWithSiwe } from "../service/main/api";
import { useUserContext } from "../context/UserContext";

const Card = styled.div`
  width: 100%;
  border-radius: 16px;
  padding: 16px;
  background: rgba(0, 0, 0, 0.35);
  z-index: 2;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  color: ${(p) => p.theme.colors.light};
`;

const Text = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${(p) => p.theme.colors.light};
  opacity: 0.9;
`;

const Button = styled.button`
  width: 100%;
  padding: 12px 14px;
  border-radius: 12px;
  border: 0;
  background: ${(p) => p.theme.colors.primary};
  color: ${(p) => p.theme.colors.light};
  font-weight: 700;
  cursor: pointer;
  opacity: ${(p) => (p.disabled ? 0.6 : 1)};
`;

const SecondaryButton = styled(Button)`
  background: ${(p) => p.theme.colors.secondary};
`;

const ErrorBox = styled.div`
  width: 100%;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(255, 0, 0, 0.12);
  color: ${(p) => p.theme.colors.light};
  font-size: 13px;
`;

function truncateAddress(addr) {
  if (!addr) return "";
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, fetchingUser, setUserData } = useUserContext();

  const referrer = useMemo(() => {
    const raw = new URLSearchParams(location.search || "").get("ref");
    if (!raw) return undefined;
    const trimmed = String(raw).trim();
    if (!trimmed) return undefined;
    if (/^\d+$/.test(trimmed)) return parseInt(trimmed, 10);
    return trimmed;
  }, [location.search]);

  const { address, isConnected, chainId: accountChainId } = useAccount();
  const chainId = useChainId();

  const { connectors, connectAsync, isPending: isConnecting } = useConnect();
  const { disconnectAsync } = useDisconnect();
  const { signMessageAsync } = useSignMessage();
  const { switchChainAsync, isPending: isSwitching } = useSwitchChain();

  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (fetchingUser) return;
    if (!user) return;
    const target = location?.state?.from || "/";
    navigate(target, { replace: true });
  }, [fetchingUser, user, navigate, location]);

  const injectedConnector = useMemo(() => connectors?.[0], [connectors]);

  const getConnectedAddress = async () => {
    if (address) return address;

    try {
      if (injectedConnector?.getAccounts) {
        const accounts = await injectedConnector.getAccounts();
        if (accounts?.[0]) return accounts[0];
      }
    } catch (_) {
      // ignore
    }

    try {
      const eth = window?.ethereum;
      if (eth?.request) {
        const accounts = await eth.request({ method: "eth_accounts" });
        if (accounts?.[0]) return accounts[0];
      }
    } catch (_) {
      // ignore
    }

    return undefined;
  };

  const ensureConnected = async () => {
    if (!injectedConnector) throw new Error("No wallet connector found");

    if (isConnected) {
      const current = await getConnectedAddress();
      if (current) return current;
      // In some cases (hard refresh / connector restore) wagmi marks connected
      // before the address is hydrated. Give it a short moment.
      for (let i = 0; i < 10; i++) {
        await new Promise((r) => setTimeout(r, 50));
        const retry = await getConnectedAddress();
        if (retry) return retry;
      }
      throw new Error("Wallet connected but address not ready yet. Please try again.");
    }

    const result = await connectAsync({ connector: injectedConnector });
    const connected = result?.accounts?.[0] || result?.account || result?.address;
    if (connected) return connected;

    const fallback = await getConnectedAddress();
    if (fallback) return fallback;
    throw new Error("Connected but no wallet address found");
  };

  const ensureBscChain = async () => {
    console.log("Current chainId:", accountChainId);
    if (accountChainId === BSC_CHAIN_ID) return;
    console.log("Switching to BSC chain...",BSC_CHAIN_ID);
    await switchChainAsync({ chainId: BSC_CHAIN_ID });
  };

  const handleLogin = async () => {
    setError("");
    setIsLoggingIn(true);
    try {
      const connectedAddress = await ensureConnected();
      try {
        await ensureBscChain();
      } catch (error) {
        alert("Please switch to BSC chain to continue");
        throw error;
      }
      if (!connectedAddress) throw new Error("No wallet address found");
      if(isSwitching){
        alert("Switching chain, please click Sign In again after switch is complete");
        throw new Error("Switching chain");
      }
      const nonceRes = await getSiweNonce();
      const nonce = nonceRes?.data?.nonce;
      if (!nonce) throw new Error("Nonce not received");

      const domain = window.location.hostname;
      const uri = window.location.origin;
      const scheme = window.location.protocol.replace(":", "");

      const siweMessage = new SiweMessage({
        scheme,
        domain,
        address: connectedAddress,
        statement: "Sign in.",
        uri,
        version: "1",
        chainId: BSC_CHAIN_ID,
        nonce,
      });

      const messageToSign = siweMessage.prepareMessage();
      const signature = await signMessageAsync({ message: messageToSign });

      const loginRes = await loginWithSiwe({ message: messageToSign, signature, referrer });
      if (!loginRes?.success) throw new Error(loginRes?.message || "Login failed");

      setUserData(loginRes.data);
      navigate("/", { replace: true });
    } catch (e) {
        console.error("Login error:", e);
      setError(e?.message || "Login failed");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleDisconnect = async () => {
    setError("");
    try {
      await disconnectAsync();
    } catch (e) {
        console.error("Disconnect error:", e);
      setError(e?.message || "Disconnect failed");
    }
  };

  const needsBsc = isConnected && chainId !== BSC_CHAIN_ID;
  const busy = isConnecting || isSwitching || isLoggingIn;

  return (
    <Page>
      <Card>
        <FlexContainer flexdirection="column" gap="12px">
          <Title>Login</Title>
          <Text>
            {isConnected ? `Connected: ${truncateAddress(address)}` : "Connect your wallet to continue."}
          </Text>

          {needsBsc && <Text>Please switch to BSC (chainId 56).</Text>}

          {error && <ErrorBox>{error}</ErrorBox>}

          {!isConnected ? (
            <Button disabled={busy} onClick={handleLogin}>
              Connect & Sign In
            </Button>
          ) : (
            <>
              <Button disabled={busy} onClick={handleLogin}>
                Sign In
              </Button>
              <SecondaryButton disabled={busy} onClick={handleDisconnect}>
                Disconnect
              </SecondaryButton>
            </>
          )}
        </FlexContainer>
      </Card>
    </Page>
  );
};

export default Login;
